/**
 * Product image gallery (converted from the Webflow detail_product thumbnail
 * list + lightbox). A large main image with a thumbnail strip below it; clicking
 * a thumbnail cross-fades the main image (GSAP). Clicking the main image opens a
 * custom lightbox overlay (React state + GSAP fade) with prev / next / close.
 *
 * The active image is controlled by the parent so selecting a colour variant can
 * drive the gallery; thumbnails report their index back up.
 */
import type { CSSProperties, FC } from 'react'

import { useEffect, useRef, useState } from 'react'

import { gsap } from '@/lib/gsap'

/* Custom lightbox overlay (replaces the Webflow JS lightbox). Inline styles
 * keep the conversion contained to the product components without touching the
 * shared stylesheets. */
const LIGHTBOX_OVERLAY: CSSProperties = {
    display: 'none',
    position: 'fixed',
    inset: 0,
    zIndex: 2000,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    backgroundColor: 'rgba(0, 0, 0, 0.88)'
}

const LIGHTBOX_FIGURE: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '90vw',
    maxHeight: '90vh'
}

const LIGHTBOX_IMAGE: CSSProperties = {
    maxWidth: '90vw',
    maxHeight: '90vh',
    objectFit: 'contain'
}

const LIGHTBOX_CONTROL: CSSProperties = {
    position: 'absolute',
    zIndex: 2001,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    borderRadius: 4,
    cursor: 'pointer',
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.12)'
}

const LIGHTBOX_CLOSE: CSSProperties = { ...LIGHTBOX_CONTROL, top: 24, right: 24 }
const LIGHTBOX_LEFT: CSSProperties = { ...LIGHTBOX_CONTROL, top: '50%', left: 24, transform: 'translateY(-50%)' }
const LIGHTBOX_RIGHT: CSSProperties = { ...LIGHTBOX_CONTROL, top: '50%', right: 24, transform: 'translateY(-50%)' }

interface ProductGalleryProps {
    images: string[]
    name: string
    activeIndex: number
    onSelect: (index: number) => void
}

export const ProductGallery: FC<ProductGalleryProps> = ({ images, name, activeIndex, onSelect }) => {
    const mainImageRef = useRef<HTMLImageElement>(null)
    const lightboxRef = useRef<HTMLDivElement>(null)
    const lightboxImageRef = useRef<HTMLImageElement>(null)

    const [lightboxOpen, setLightboxOpen] = useState(false)
    const mainFirstRun = useRef(true)
    const lightboxFirstRun = useRef(true)

    const safeIndex = Math.min(Math.max(activeIndex, 0), Math.max(images.length - 1, 0))
    const current = images[safeIndex] || images[0] || ''

    const step = (delta: number) => {
        if (images.length < 2) return
        const next = (safeIndex + delta + images.length) % images.length
        onSelect(next)
    }

    /* Cross-fade the main image whenever the active index changes. */
    useEffect(() => {
        const image = mainImageRef.current
        if (!image) return

        if (mainFirstRun.current) {
            mainFirstRun.current = false
            return
        }

        gsap.fromTo(image, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power1.inOut' })
    }, [safeIndex])

    /* Lightbox open / close fade. */
    useEffect(() => {
        const overlay = lightboxRef.current
        const image = lightboxImageRef.current
        if (!overlay) return

        if (lightboxOpen) {
            lightboxFirstRun.current = false
            document.body.style.overflow = 'hidden'
            gsap.set(overlay, { display: 'flex' })
            gsap.fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power1.inOut' })
            if (image) gsap.fromTo(image, { opacity: 0, scale: 0.96 }, { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out' })
        } else if (!lightboxFirstRun.current) {
            document.body.style.overflow = ''
            gsap.to(overlay, {
                opacity: 0,
                duration: 0.3,
                ease: 'power1.inOut',
                onComplete: () => gsap.set(overlay, { display: 'none' })
            })
        }
    }, [lightboxOpen])

    /* Fade the lightbox image when paging while open. */
    useEffect(() => {
        if (!lightboxOpen) return
        const image = lightboxImageRef.current
        if (!image) return

        gsap.fromTo(image, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'power1.inOut' })
    }, [safeIndex, lightboxOpen])

    /* Restore scroll if unmounted while the lightbox is open. */
    useEffect(() => {
        return () => {
            document.body.style.overflow = ''
        }
    }, [])

    /* Keyboard controls while the lightbox is open. */
    useEffect(() => {
        if (!lightboxOpen) return

        const onKey = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') setLightboxOpen(false)
            if (event.key === 'ArrowRight') step(1)
            if (event.key === 'ArrowLeft') step(-1)
        }

        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lightboxOpen, safeIndex, images.length])

    return (
        <div className='product-image-container'>
            <div
                className='product-main-image-holder'
                role='button'
                tabIndex={0}
                aria-label={`Open ${name} image`}
                style={{ cursor: 'zoom-in' }}
                onClick={() => setLightboxOpen(true)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        setLightboxOpen(true)
                    }
                }}
            >
                <img ref={mainImageRef} alt={name} loading='lazy' src={current} className='product-image-main' />
            </div>

            <div className='product-thumbnail-images'>
                <div className='thumbnails-list-wrapper w-dyn-list'>
                    <div role='list' className='thumbnails-list w-dyn-items'>
                        {images.map((image, index) => (
                            <div key={`${image}-${index}`} role='listitem' className='thumbnails-item w-dyn-item'>
                                <a
                                    href='#'
                                    className='thumbnail-image-holder w-inline-block'
                                    aria-label={`View image ${index + 1} of ${name}`}
                                    aria-current={index === safeIndex}
                                    onClick={(event) => {
                                        event.preventDefault()
                                        onSelect(index)
                                    }}
                                >
                                    <img alt='' loading='lazy' src={image} className='thumbnail-image' />
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div
                ref={lightboxRef}
                role='dialog'
                aria-modal='true'
                aria-label={`${name} images`}
                style={LIGHTBOX_OVERLAY}
                onClick={() => setLightboxOpen(false)}
            >
                <div
                    role='button'
                    tabIndex={0}
                    aria-label='Close'
                    style={LIGHTBOX_CLOSE}
                    onClick={(event) => {
                        event.stopPropagation()
                        setLightboxOpen(false)
                    }}
                >
                    <svg width='20' height='20' viewBox='0 0 16 16' aria-hidden='true'>
                        <polygon
                            fill='currentColor'
                            points='6.23223305 8 0.616116524 13.6161165 2.38388348 15.3838835 8 9.76776695 13.6161165 15.3838835 15.3838835 13.6161165 9.76776695 8 15.3838835 2.38388348 13.6161165 0.616116524 8 6.23223305 2.38388348 0.616116524 0.616116524 2.38388348 6.23223305 8'
                        />
                    </svg>
                </div>

                {images.length > 1 && (
                    <div
                        role='button'
                        tabIndex={0}
                        aria-label='Previous image'
                        style={LIGHTBOX_LEFT}
                        onClick={(event) => {
                            event.stopPropagation()
                            step(-1)
                        }}
                    >
                        <img src='/images/Chevron-Left.svg' loading='lazy' alt='' style={{ width: 12 }} />
                    </div>
                )}

                <div style={LIGHTBOX_FIGURE} onClick={(event) => event.stopPropagation()}>
                    <img ref={lightboxImageRef} src={current} alt={name} style={LIGHTBOX_IMAGE} />
                </div>

                {images.length > 1 && (
                    <div
                        role='button'
                        tabIndex={0}
                        aria-label='Next image'
                        style={LIGHTBOX_RIGHT}
                        onClick={(event) => {
                            event.stopPropagation()
                            step(1)
                        }}
                    >
                        <img src='/images/Chevron-Right.svg' loading='lazy' alt='' style={{ width: 12 }} />
                    </div>
                )}
            </div>
        </div>
    )
}
